import * as Sentry from '@sentry/nextjs';

import {
  MarketplacePropertyListFragment,
  MarketplacePropertySearchFragment,
} from '__generated__/athena';

import { fetchMainSiteHeaderAndFooter } from 'cms';

import {
  buildMarketInfo,
  fetchActiveBuyMarkets,
  fetchPropertyList,
  fetchPropertySearch,
  tryGetMarketCoordinates,
} from 'components/marketplace/helpers/ssr';
import {
  parseQueryParams,
  PropertySearchQuery,
} from 'components/marketplace/helpers/useSearchQueries';
import { MarketInfo } from 'components/marketplace/map/types';
import { DEFAULT_MARKET } from 'components/marketplace/search/utils';
import { ActiveMarket } from 'components/marketplace/types';

import { ISiteFooter, ISiteHeader } from 'declarations/contentful';
import { CacheSafeGetServerSideProps } from 'declarations/shared/serverSideProps';

export { default } from './homes/[market]';

export const getServerSideProps: CacheSafeGetServerSideProps<{
  propertyList: MarketplacePropertyListFragment;
  marketInfo: MarketInfo;
  propertySearch: MarketplacePropertySearchFragment;
  queryParams: PropertySearchQuery;
  activeMarkets: ActiveMarket[];
  header: ISiteHeader | null;
  footer: ISiteFooter | null;
  canonicalOverride: string | null;
}> = async (context) => {
  const queryParams = parseQueryParams(context.query);
  const mParam = context.query.m;
  const market = (Array.isArray(mParam) ? mParam[0] : mParam) || DEFAULT_MARKET.toLowerCase();

  const marketRes = await tryGetMarketCoordinates(context, market);

  if (!marketRes) {
    Sentry.captureMessage('homes hub: market coordinate lookup failed', {
      level: 'warning',
      extra: { requestedMarket: market },
    });
    return {
      redirect: {
        destination: `/homes/${DEFAULT_MARKET.toLowerCase()}`,
        permanent: false,
      },
    };
  }

  const resolvedMarket = marketRes.resolvedMarket;

  const [propertyListRes, propertySearchRes, activeMarkets, { header, footer }] = await Promise.all(
    [
      fetchPropertyList(queryParams, resolvedMarket, undefined, context.req.headers),
      fetchPropertySearch(queryParams, resolvedMarket, undefined, context.req.headers),
      fetchActiveBuyMarkets(context.req.headers),
      fetchMainSiteHeaderAndFooter(),
    ],
  );

  const marketInfo = buildMarketInfo(undefined, marketRes.query, queryParams, resolvedMarket);
  const propertyList = propertyListRes ? propertyListRes.marketplace.propertyList : undefined;
  const propertySearch = propertySearchRes
    ? propertySearchRes.marketplace.propertySearch
    : undefined;

  if (!marketInfo || !propertyList || !propertySearch) {
    Sentry.captureMessage('homes hub: SSR data fetch failed, falling back to default market', {
      level: 'warning',
      extra: {
        resolvedMarket,
        hasMarketInfo: !!marketInfo,
        hasPropertyList: !!propertyList,
        hasPropertySearch: !!propertySearch,
      },
    });
    return {
      redirect: {
        destination: `/homes/${DEFAULT_MARKET.toLowerCase()}`,
        permanent: false,
      },
    };
  }

  return {
    props: {
      propertyList,
      marketInfo,
      propertySearch,
      queryParams,
      activeMarkets,
      header,
      footer,
      canonicalOverride: '/homes',
    },
  };
};
